import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { GlobalAppointment, GlobalAppointmentsTotals } from '@erp/shared';
import { Input, Label, Select } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { pageCountOf, Pagination } from '@/components/ui/pagination';
import { formatMinutes } from '@/lib/utils';
import {
  TIMESHEET_PAGE_SIZE,
  timesheetQueryOptions,
  timesheetTotalsQueryOptions,
} from './appointments-queries';
import { teamListQuery } from '@/features/team/team-queries';

// ── Search schema (validateSearch runs in the router — keep it pure) ────────
function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function firstDayOfCurrentMonth(): string {
  const now = new Date();
  return toISODate(new Date(now.getFullYear(), now.getMonth(), 1));
}

function todayISO(): string {
  return toISODate(new Date());
}

export const appointmentsSearchSchema = z.object({
  start: z.string().default(firstDayOfCurrentMonth),
  end: z.string().default(todayISO),
  user: z.coerce.number().int().positive().optional().catch(undefined),
  /** página da lista (1…); os totais são sempre do período inteiro */
  page: z.coerce.number().int().min(1).default(1).catch(1),
});
export type AppointmentsSearch = z.infer<typeof appointmentsSearchSchema>;

// ── Helpers ─────────────────────────────────────────────────────────────────
function formatDate(value: string | Date): string {
  return new Date(value).toLocaleDateString('pt-BR');
}

function formatTime(value: string | Date): string {
  return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** null = timer em andamento (sem endedAt). */
function durationMinutes(a: GlobalAppointment): number | null {
  if (a.endedAt === null) return null;
  const ms = new Date(a.endedAt).getTime() - new Date(a.startedAt).getTime();
  return Math.max(0, Math.round(ms / 60_000));
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}

interface UserGroup {
  userId: number;
  userName: string;
  rows: GlobalAppointment[];
}

type UserTotals = GlobalAppointmentsTotals['byUser'][number];

/**
 * Agrupa por pessoa as linhas DA PÁGINA. Os totais de cada pessoa não saem
 * daqui: somar a página daria o total da página, e numa folha de horas o
 * total é o do período — vem do GET /appointments/totals.
 */
function groupByUser(items: GlobalAppointment[]): UserGroup[] {
  const map = new Map<number, UserGroup>();
  for (const item of items) {
    let group = map.get(item.user.id);
    if (!group) {
      group = { userId: item.user.id, userName: item.user.name, rows: [] };
      map.set(item.user.id, group);
    }
    group.rows.push(item);
  }
  const groups = [...map.values()];
  for (const group of groups) {
    group.rows.sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());
  }
  groups.sort((a, b) => a.userName.localeCompare(b.userName, 'pt-BR'));
  return groups;
}

// ── Chips locais (pill de duração teal + "em andamento" pulsante) ───────────
function DurationPill({ minutes }: { minutes: number }) {
  return (
    <span className="inline-flex rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground tabular-nums">
      {formatMinutes(minutes)}
    </span>
  );
}

function RunningChip({ label = 'em andamento' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-sm border border-[#FFE58F] bg-[#FFFBE6] px-2 py-0.5 text-xs font-medium text-[#D48806]">
      <span className="relative flex size-1.5" aria-hidden>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-75" />
        <span className="relative inline-flex size-1.5 rounded-full bg-warning" />
      </span>
      {label}
    </span>
  );
}

function GroupSkeleton() {
  return (
    <div aria-busy className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="flex items-center gap-3 border-b border-divider px-4 py-3">
        <Skeleton className="size-8 rounded-full" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="ml-auto h-5 w-16 rounded-full" />
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-6 border-b border-divider px-4 py-3 last:border-0">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
      ))}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export function AppointmentsPage() {
  const search = useSearch({ from: '/_authenticated/appointments' });
  const navigate = useNavigate();

  // o filtro "Usuário": a equipe deste tenant, inativos inclusive (quem saiu
  // continua com apontamento no período) — pedido da PoolTrack, 05/10
  const users = useQuery(teamListQuery({ includeInactive: true }));
  const filters = { start: search.start, end: search.end, user: search.user };
  const timesheet = useQuery(timesheetQueryOptions({ ...filters, page: search.page }));
  const totals = useQuery(timesheetTotalsQueryOptions(filters));

  /** Filtro novo recomeça da primeira página. */
  function updateFilters(patch: Partial<Omit<AppointmentsSearch, 'page'>>) {
    void navigate({ to: '/appointments', search: { ...search, ...patch, page: 1 } });
  }

  function goToPage(page: number) {
    void navigate({ to: '/appointments', search: { ...search, page } });
  }

  const groups = timesheet.data ? groupByUser(timesheet.data.items) : [];
  const total = timesheet.data?.total ?? 0;
  const pageCount = pageCountOf(total, TIMESHEET_PAGE_SIZE);
  const totalsByUser = new Map((totals.data?.byUser ?? []).map((u) => [u.user.id, u]));

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Apontamentos</h1>
        {totals.data ? (
          <span className="flex items-center gap-2 text-[13px] text-muted-foreground">
            Total do período
            <DurationPill minutes={totals.data.totalMinutes} />
          </span>
        ) : totals.isPending ? (
          <Skeleton className="h-5 w-24 rounded-full" />
        ) : (
          <span className="text-[13px] text-destructive">
            Não foi possível carregar os totais do período.
          </span>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-4 shadow-xs">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ts-start" className="text-[13px]">
            Início
          </Label>
          <Input
            id="ts-start"
            type="date"
            className="h-8 w-40"
            value={search.start}
            onChange={(e) => {
              if (e.target.value) updateFilters({ start: e.target.value });
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ts-end" className="text-[13px]">
            Fim
          </Label>
          <Input
            id="ts-end"
            type="date"
            className="h-8 w-40"
            value={search.end}
            onChange={(e) => {
              if (e.target.value) updateFilters({ end: e.target.value });
            }}
          />
        </div>
        <div className="flex min-w-48 flex-col gap-1.5">
          <Label htmlFor="ts-user" className="text-[13px]">
            Usuário
          </Label>
          <Select
            id="ts-user"
            className="h-8"
            value={search.user !== undefined ? String(search.user) : ''}
            onChange={(e) =>
              updateFilters({ user: e.target.value ? Number(e.target.value) : undefined })
            }
          >
            <option value="">Todos os usuários</option>
            {(users.data?.items ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {timesheet.isPending ? (
        <div className="space-y-4">
          <GroupSkeleton />
          <GroupSkeleton />
        </div>
      ) : timesheet.error ? (
        <div className="rounded-lg border bg-card shadow-xs">
          <EmptyState message="Erro ao carregar apontamentos. Tente novamente." />
        </div>
      ) : total === 0 ? (
        <div className="rounded-lg border bg-card shadow-xs">
          <EmptyState className="h-52" message="Nenhum apontamento no período selecionado" />
        </div>
      ) : (
        <div className="space-y-4">
          {groups.length === 0 ? (
            // página além da última (URL editada, ou o período encolheu)
            <div className="rounded-lg border bg-card shadow-xs">
              <EmptyState className="h-40" message="Nenhum apontamento nesta página" />
            </div>
          ) : null}
          {groups.map((group) => (
            <UserSection
              key={group.userId}
              group={group}
              totals={totalsByUser.get(group.userId)}
              totalsPending={totals.isPending}
            />
          ))}

          {/* Rodapé: contagem + paginação (o padrão da tela de clientes) */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-xs">
            <span className="text-[13px] text-muted-foreground">
              {total} apontamento{total === 1 ? '' : 's'} — página {search.page} de {pageCount}
            </span>
            <Pagination page={search.page} pageCount={pageCount} onPage={goToPage} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Uma pessoa: as linhas dela NESTA página, com os números do período inteiro
 * no cabeçalho — os mesmos em toda página em que ela aparecer.
 */
function UserSection({
  group,
  totals,
  totalsPending,
}: {
  group: UserGroup;
  totals: UserTotals | undefined;
  totalsPending: boolean;
}) {
  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-xs">
      {/* Cabeçalho do usuário */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <Avatar>
          <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
            {initials(group.userName)}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm font-semibold">{group.userName}</span>
        {totals && totals.runningCount > 0 ? (
          <RunningChip
            label={
              totals.runningCount === 1 ? '1 em andamento' : `${totals.runningCount} em andamento`
            }
          />
        ) : null}
        <div className="ml-auto flex items-center gap-2 text-[13px] text-muted-foreground">
          {totals ? (
            <>
              <span className="tabular-nums">
                {totals.count} apontamento{totals.count === 1 ? '' : 's'}
              </span>
              <span aria-hidden>·</span>
              Total do período
              <DurationPill minutes={totals.totalMinutes} />
            </>
          ) : totalsPending ? (
            <Skeleton className="h-5 w-32 rounded-full" />
          ) : (
            '—'
          )}
        </div>
      </div>
      {/* Tabela */}
      <div className="overflow-x-auto border-t border-divider">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted text-left text-[13px]">
              <th className="h-9 px-4 font-semibold">Data</th>
              <th className="h-9 px-4 font-semibold">Ticket</th>
              <th className="h-9 px-4 font-semibold">Mesa</th>
              <th className="h-9 px-4 font-semibold">Período</th>
              <th className="h-9 px-4 font-semibold">Duração</th>
              <th className="h-9 px-4 font-semibold">Descrição</th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row) => {
              const minutes = durationMinutes(row);
              return (
                <tr
                  key={row.id}
                  className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                >
                  <td className="px-4 whitespace-nowrap tabular-nums">{formatDate(row.startedAt)}</td>
                  <td className="px-4">
                    <Link
                      to="/tickets/$ticketNumber"
                      params={{ ticketNumber: String(row.ticketNumber) }}
                      className="font-medium text-primary tabular-nums hover:text-primary-pressed hover:underline"
                    >
                      #{row.ticketNumber}
                    </Link>
                    <span
                      className="ml-2 inline-block max-w-56 truncate align-bottom text-muted-foreground"
                      title={row.ticketTitle}
                    >
                      {row.ticketTitle}
                    </span>
                  </td>
                  <td className="max-w-40 truncate px-4 text-muted-foreground">{row.desk.name}</td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatTime(row.startedAt)}
                    {' – '}
                    {row.endedAt !== null ? formatTime(row.endedAt) : '…'}
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    {minutes !== null ? <DurationPill minutes={minutes} /> : <RunningChip />}
                  </td>
                  <td className="px-4">
                    <span
                      className="block max-w-72 truncate text-muted-foreground"
                      title={row.description ?? undefined}
                    >
                      {row.description ?? '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
